import React, { useState } from 'react';
import { Phone, MessageSquare, Users, Calendar, Settings } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { OutboundCallDialer } from './OutboundCallDialer';
import { IncomingCallHandler } from './IncomingCallHandler';
import { IncomingCallQueue } from './IncomingCallQueue';
import { CallHistory } from './CallHistory';
import { MeetingScheduler } from './MeetingScheduler';
import { ChatFileUpload } from './ChatFileUpload';
import { useCallCenter } from '@/hooks/useCallCenter';
import { cn } from '@/lib/utils';

interface CallCenterPanelProps {
  className?: string;
  dealId?: string;
}

export function CallCenterPanel({ className, dealId }: CallCenterPanelProps) {
  const { activeCalls, stats, loading } = useCallCenter();
  const [activeTab, setActiveTab] = useState('calls');

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Stats bar */}
      <div className="flex items-center gap-4 p-4 bg-muted/30 border-b">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-sm font-medium">Centre d'appels</span>
        </div>
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span>
            <Badge variant="secondary" className="mr-1">{stats.activeCalls}</Badge>
            Actifs
          </span>
          <span>
            <Badge variant="outline" className="mr-1">{stats.callsInQueue}</Badge>
            En attente
          </span>
          <span>
            <Badge variant="outline" className="mr-1">{stats.agentsOnline}</Badge>
            Agents en ligne
          </span>
        </div>
      </div>

      {/* Main content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-4">
          <TabsTrigger value="calls" className="gap-2">
            <Phone className="h-4 w-4" />
            Appels
          </TabsTrigger>
          <TabsTrigger value="incoming" className="gap-2">
            <Phone className="h-4 w-4" />
            Entrants
            {activeCalls.filter(c => c.status === 'ringing').length > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 justify-center">
                {activeCalls.filter(c => c.status === 'ringing').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="team" className="gap-2">
            <Users className="h-4 w-4" />
            Équipe
          </TabsTrigger>
          <TabsTrigger value="schedule" className="gap-2">
            <Calendar className="h-4 w-4" />
            Horaires
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-auto p-4">
          <TabsContent value="calls" className="mt-0 space-y-4">
            {/* Call queue - realtime */}
            <IncomingCallQueue />

            <div className="grid lg:grid-cols-2 gap-4">
              <OutboundCallDialer
                onCallStarted={(callSid) => console.log('Call started:', callSid)}
                onCallEnded={() => console.log('Call ended')}
              />
              
              {/* Active calls list */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" />
                    Appels actifs
                    <Badge variant="secondary">{activeCalls.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-4 text-muted-foreground">
                      Chargement...
                    </div>
                  ) : activeCalls.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      Aucun appel actif
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {activeCalls.map(call => (
                        <div
                          key={call.id}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium">
                              {call.participants[0]?.name || 'Inconnu'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {call.participants[0]?.phone}
                            </p>
                          </div>
                          <Badge variant={call.status === 'active' ? 'default' : 'secondary'}>
                            {call.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Call history */}
            <CallHistory dealId={dealId} className="mt-4" />
          </TabsContent>

          <TabsContent value="incoming" className="mt-0">
            <IncomingCallHandler
              onAnswer={(call) => console.log('Answered:', call)}
              onDecline={(call) => console.log('Declined:', call)}
            />
          </TabsContent>

          <TabsContent value="team" className="mt-0">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Équipe disponible
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Gestion des agents à venir</p>
                  <p className="text-sm">
                    Configurez les agents et leur disponibilité
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule" className="mt-0">
            <div className="max-w-2xl">
              <MeetingScheduler dealId={dealId} />
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
